import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { Box } from '@mui/material';

// comps
import DialogTemplate from '../../components/dialogs/dialogTemplate';
import GlobalInput from '../../components/global/input';
import GlobalButton from '../../components/global/button';
import GlobalDropdown from '../../components/global/dropdown';
import ToastMessage from '../../components/global/toastMessage';

// icons
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-icon.svg';
import GlobalRadioGroup, { GlobalRadioGroupOptions } from '../../components/global/radio';
import GlobalCheckbox from '../../components/global/checkbox';
import {CompanyDataItem} from "./company-dialog";
import {width} from "@mui/system";

export interface ClientDataItem {
    id?: number;
    company: CompanyDataItem;
    subCompanyName: string;
    firstName: string;
    lastName: string;
    address: string;
    contactEmail: string;
    contactPhone: string;
    comment: string;
    lastActivityDate: Date;
    isActive: boolean;
}

interface ClientDialogProps {
    data: ClientDataItem;
    onSave: (client: ClientDataItem) => void;
    onClose: () => void;
    companies: any;
    onCreate: (client: ClientDataItem) => void;
}

const ClientDialog = (props: ClientDialogProps) => {

    const { data, onSave, onClose, companies, onCreate } = props;
    const isCreate = !data.id;

    const [errorMsg, setErrorMsg] = useState(null);
    const [requiredFields, setRequiredFields] = useState([]);
    const [isClicked, setIsClicked] = useState(false);

    const [dataItem, setDataItem] = useState(data);

    const onChange = (name: any, value: any) => {
        setDataItem({
            ...dataItem,
            [name]: value,
        });
    };


    useEffect(() => {
        const requiredFieldsArr = ['company', 'firstName', 'lastName', 'contactEmail'];
        setRequiredFields(requiredFieldsArr.filter(field => {
            if (typeof dataItem[field] === 'string' && dataItem[field].trim() === '') return true;

            if (typeof dataItem[field] === 'number' && isNaN(dataItem[field])) return true;

            if (field === 'company')
                return !dataItem['company']?.id;

            return (dataItem[field] === null || dataItem[field] === undefined);
        }));
    }, [dataItem]);

    const handleSave = () => {

        let item = { ...dataItem };

        setIsClicked((state) => !state);

        if (requiredFields.length === 0) {
            if (isCreate) onCreate(item);
            else {
                // If fields were NOT edited (= if the object is unchanged)
                if (_.isEqual(item, data)) {
                    toast.warning(`There were no changes made`);
                    onClose();
                } else {
                    onSave(item);
                }
            }
        } else {
            setErrorMsg(`The required fields must be filled in order to proceed.`);
        }
    };

    const handleErrorState = (name: string) => {
        if (isClicked) {
            return requiredFields?.includes(name);
        }
    };

    const renderContent = () => {
        return (
            <Container>
                {errorMsg && (
                    <FlexBox>
                        <ToastMessage isError msg={errorMsg} icon={<WarningIcon />} />
                    </FlexBox>
                )}
                <FlexBox sx={{ flexDirection: 'row', mb: '5px' }}>
                    <FlexBox sx={{ flexDirection: 'column', width: '50%'}}>
                        <Label>Company <span>*</span></Label>
                        <GlobalDropdown
                            isRequired
                            isSearch
                            isError={handleErrorState('company')}
                            placeholder={isCreate ? 'Select Company' : ''}
                            data={companies}
                            field={'id'}
                            name={'name'}
                            dataItemKey={'company'}
                            value={dataItem.company}
                            onChange={onChange}
                            sx={{ width: '100%' }}
                        />
                    </FlexBox>
                    <FlexBox sx={{ flexDirection: 'column', width: '50%', '& > *': { width: '100%' } }}>
                        <Label>SubCompany (Optional)</Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('subCompanyName')}
                            placeholder="Sub Company"
                            value={dataItem.subCompanyName}
                            name={'subCompanyName'}
                            onChange={onChange}
                            sx={{ width: '100%' }}
                        />
                    </FlexBox>
                </FlexBox>
                <FlexBox sx={{ flexDirection: 'row', mb: '5px'}}>
                    <FlexBox sx={{ flexDirection: 'column', width: '50%', '& > *': { width: '100%' } }}>
                        <Label>First Name <span>*</span></Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('firstName')}
                            placeholder="First Name"
                            value={dataItem.firstName}
                            name={'firstName'}
                            onChange={onChange}
                            sx={{ width: '100%' }}
                        />
                    </FlexBox>
                    <FlexBox sx={{ flexDirection: 'column', width: '50%', '& > *': { width: '100%' } }}>
                        <Label>Last Name <span>*</span></Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('lastName')}
                            placeholder="Last Name"
                            value={dataItem.lastName}
                            name={'lastName'}
                            onChange={onChange}
                            sx={{ width: '100%' }}
                        />
                    </FlexBox>
                </FlexBox>
                <FlexBox sx={{ flexDirection: 'row', mb: '5px' }}>
                    <FlexBox sx={{ flexDirection: 'column', width: '100%', '& > *': { width: '100%' } }}>
                        <Label>Address (Optional)</Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('address')}
                            placeholder="Address"
                            value={dataItem.address}
                            name={'address'}
                            onChange={onChange}
                        />
                    </FlexBox>
                </FlexBox>
                <FlexBox sx={{ flexDirection: 'row', mb: '5px' }}>
                    <FlexBox sx={{ flexDirection: 'column', width: '50%', '& > *': { width: '100%' } }}>
                        <Label>Contact Email <span>*</span></Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('contactEmail')}
                            placeholder="Contact Email"
                            value={dataItem.contactEmail}
                            name={'contactEmail'}
                            onChange={onChange}
                        />
                    </FlexBox>
                    <FlexBox sx={{ flexDirection: 'column', width: '30%', '& > *': { width: '100%' } }}>
                        <Label>Contact Phone (Optional)</Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('contactPhone')}
                            placeholder="Contact Phone"
                            value={dataItem.contactPhone}
                            name={'contactPhone'}
                            onChange={onChange}
                        />
                    </FlexBox>
                    <FlexBox sx={{ flexDirection: 'column', width: '20%'}}>
                        <GlobalCheckbox 
                            name={'isActive'} 
                            onChange={onChange} 
                            label={'Active'} 
                            isChecked={dataItem.isActive} 
                            useCheckedValue={true} 
                        />
                    </FlexBox>
                </FlexBox>
                <FlexBox sx={{ flexDirection: 'row' }}>
                    <FlexBox sx={{ flexDirection: 'column', width: '100%', '& > *': { width: '100%' } }}>
                        <Label>Client Comment (Optional)</Label>
                        <GlobalInput
                            multiline
                            minRows={3}
                            placeholder="Leave a comment"
                            value={dataItem.comment}
                            name={'comment'}
                            onChange={onChange}
                            sx={{ mb: '1em', width: '100%' }}
                        />
                    </FlexBox>
                </FlexBox>
                <FlexBox>
                    <GlobalButton text="cancel" variant="outlined" onClick={onClose} />
                    <GlobalButton text={isCreate ? 'create' : 'save'} variant="contained" onClick={handleSave} />
                </FlexBox>
            </Container>
        );
    };

    return (
        <>
            <DialogTemplate {...props} title={'Client'} renderContent={renderContent} />
        </>
    );
};

export default ClientDialog;

const Container = styled(Box)`
    && {
        width: 100%;
        padding: 0px;
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0px 2px
    }
`;

const InputBox = styled(Box)`
    && {
        width: 100%;
        & > .MuiBox-root {
            margin-bottom: 1em;
        }
    }
`;

const Label = styled.div`
    color: #0085E6;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 2px;

    & > span {
        color: #D73948;
    }
`
